import { useState, useRef, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Product } from '../../types'
import { localDb } from '../../lib/db'
import { useToast } from '../../contexts/ToastContext'

export default function AIStudio() {
    const [searchParams] = useSearchParams()
    const { showToast } = useToast()

    const [selectedProduct, setSelectedProduct] = useState<Product | null>(null)
    const [products, setProducts] = useState<Product[]>([])
    const [uploadedImage, setUploadedImage] = useState<string | null>(null)
    const [generatedImage, setGeneratedImage] = useState<string | null>(null)
    const [isGenerating, setIsGenerating] = useState(false)

    const fileInputRef = useRef<HTMLInputElement>(null)

    useEffect(() => {
        const allProducts = localDb.products.getAll()
        setProducts(allProducts)

        // Check if product ID was passed in URL
        const productId = searchParams.get('product')
        if (productId) {
            const product = allProducts.find(p => p.id === productId)
            if (product) {
                setSelectedProduct(product)
                if (product.image_url) {
                    setUploadedImage(product.image_url)
                }
            }
        }
    }, [searchParams])

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        if (!file) return

        if (!file.type.startsWith('image/')) {
            showToast('Please upload an image file', 'error')
            return
        }

        const reader = new FileReader()
        reader.onload = (event) => {
            setUploadedImage(event.target?.result as string)
            setGeneratedImage(null)
        }
        reader.readAsDataURL(file)
    }

    const generateAIImage = async () => {
        if (!uploadedImage) {
            showToast('Please upload an image first', 'error')
            return
        }

        setIsGenerating(true)

        // Simulate AI generation with placeholder
        // In production, this would call an actual AI API like Replicate or HuggingFace
        await new Promise(resolve => setTimeout(resolve, 3000))

        // For demo, we'll create a simple transform effect
        // In production, replace with actual AI try-on API call
        const modelImage = 'https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=400&h=600&fit=crop'

        setGeneratedImage(modelImage)
        setIsGenerating(false)
        showToast('AI image generated! (Demo mode)', 'success')
    }

    const saveToProduct = () => {
        if (!selectedProduct || !generatedImage) {
            showToast('Select a product and generate an image first', 'error')
            return
        }

        localDb.products.update(selectedProduct.id, {
            ai_model_image_url: generatedImage
        })

        showToast('AI image saved to product!', 'success')
    }

    return (
        <div>
            <div className="admin-page-header">
                <div>
                    <h1 className="admin-page-title">✨ AI Virtual Studio</h1>
                    <p className="admin-page-subtitle">
                        Generate images of Indian models wearing your products
                    </p>
                </div>
            </div>

            <div className="studio-layout">
                {/* Left: Upload Section */}
                <div className="studio-upload">
                    <div className="section-card">
                        <h3>📷 Upload Dress Image</h3>
                        <p className="section-desc">Upload a flat-lay or mannequin image of the dress</p>

                        <div
                            className={`upload-zone ${uploadedImage ? 'has-image' : ''}`}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            {uploadedImage ? (
                                <img src={uploadedImage} alt="Uploaded dress" className="uploaded-preview" />
                            ) : (
                                <div className="upload-placeholder">
                                    <span className="upload-icon">📤</span>
                                    <p>Click to upload image</p>
                                    <span className="upload-hint">or drag and drop</span>
                                </div>
                            )}
                        </div>

                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            onChange={handleFileUpload}
                            style={{ display: 'none' }}
                        />

                        {uploadedImage && (
                            <button
                                className="btn btn-secondary change-btn"
                                onClick={() => fileInputRef.current?.click()}
                            >
                                Change Image
                            </button>
                        )}
                    </div>

                    {/* Product Selection */}
                    <div className="section-card">
                        <h3>🏷️ Link to Product (Optional)</h3>
                        <p className="section-desc">Save the generated image to a product</p>

                        <select
                            className="input"
                            value={selectedProduct?.id || ''}
                            onChange={e => {
                                const product = products.find(p => p.id === e.target.value)
                                setSelectedProduct(product || null)
                            }}
                        >
                            <option value="">Select a product...</option>
                            {products.map(product => (
                                <option key={product.id} value={product.id}>
                                    {product.name}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* Right: Generate Section */}
                <div className="studio-generate">
                    <div className="section-card generation-card">
                        <h3>🤖 AI Generated Result</h3>
                        <p className="section-desc">Indian model wearing your dress</p>

                        <div className="generation-preview">
                            {isGenerating ? (
                                <div className="generating-state">
                                    <div className="loader"></div>
                                    <p>Generating AI image...</p>
                                    <span className="generating-hint">This may take 30-60 seconds</span>
                                </div>
                            ) : generatedImage ? (
                                <img src={generatedImage} alt="AI generated" className="generated-image" />
                            ) : (
                                <div className="empty-generation">
                                    <span>✨</span>
                                    <p>Generated image will appear here</p>
                                </div>
                            )}
                        </div>

                        <div className="generation-actions">
                            <button
                                className="btn btn-primary btn-lg generate-btn"
                                onClick={generateAIImage}
                                disabled={!uploadedImage || isGenerating}
                            >
                                {isGenerating ? 'Generating...' : '✨ Generate AI Image'}
                            </button>

                            {generatedImage && (
                                <button
                                    className="btn btn-success btn-lg"
                                    onClick={saveToProduct}
                                    disabled={!selectedProduct}
                                >
                                    💾 Save to Product
                                </button>
                            )}
                        </div>
                    </div>

                    {/* How it works */}
                    <div className="how-it-works">
                        <h4>How it works</h4>
                        <ol>
                            <li>Upload a clear image of the dress (flat-lay or on mannequin)</li>
                            <li>Click "Generate AI Image" to create a model wearing it</li>
                            <li>Select a product and save the result</li>
                            <li>The AI image will show on your product page</li>
                        </ol>
                        <p className="demo-note">
                            <strong>Note:</strong> This is a demo. In production, connect to Replicate,
                            Kolors Virtual, or similar AI APIs for actual try-on generation.
                        </p>
                    </div>
                </div>
            </div>

            <style>{`
        .studio-layout {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: var(--space-6);
        }
        
        .studio-upload,
        .studio-generate {
          display: flex;
          flex-direction: column;
          gap: var(--space-4);
        }
        
        .section-card {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
          padding: var(--space-6);
        }
        
        .section-card h3 {
          font-size: var(--text-lg);
          margin-bottom: var(--space-1);
        }
        
        .section-desc {
          color: var(--text-muted);
          font-size: var(--text-sm);
          margin-bottom: var(--space-4);
        }
        
        .upload-zone {
          aspect-ratio: 3/4;
          background: var(--bg-tertiary);
          border: 2px dashed var(--border-color);
          border-radius: var(--radius-xl);
          cursor: pointer;
          overflow: hidden;
          transition: all var(--transition-fast);
          display: flex;
          align-items: center;
          justify-content: center;
        }
        
        .upload-zone:hover {
          border-color: var(--color-primary);
          background: rgba(99, 102, 241, 0.05);
        }
        
        .upload-zone.has-image {
          border-style: solid;
        }
        
        .upload-placeholder {
          text-align: center;
          color: var(--text-muted);
        }
        
        .upload-icon {
          font-size: 3rem;
          display: block;
          margin-bottom: var(--space-2);
        }
        
        .upload-hint {
          font-size: var(--text-xs);
        }
        
        .uploaded-preview {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .change-btn {
          width: 100%;
          margin-top: var(--space-4);
        }
        
        .generation-card {
          flex: 1;
        }
        
        .generation-preview {
          aspect-ratio: 3/4;
          background: var(--bg-tertiary);
          border-radius: var(--radius-xl);
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: var(--space-4);
        }
        
        .generating-state {
          text-align: center;
        }
        
        .generating-state .loader {
          margin: 0 auto var(--space-4);
        }
        
        .generating-hint {
          font-size: var(--text-xs);
          color: var(--text-muted);
        }
        
        .empty-generation {
          text-align: center;
          color: var(--text-muted);
        }
        
        .empty-generation span {
          font-size: 3rem;
          display: block;
          margin-bottom: var(--space-2);
        }
        
        .generated-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }
        
        .generation-actions {
          display: flex;
          flex-direction: column;
          gap: var(--space-3);
        }
        
        .generate-btn {
          width: 100%;
        }
        
        .how-it-works {
          background: linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(236, 72, 153, 0.1) 100%);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-xl);
          padding: var(--space-6);
        }
        
        .how-it-works h4 {
          margin-bottom: var(--space-4);
        }
        
        .how-it-works ol {
          padding-left: var(--space-6);
          margin-bottom: var(--space-4);
        }
        
        .how-it-works li {
          margin-bottom: var(--space-2);
          color: var(--text-secondary);
          font-size: var(--text-sm);
        }
        
        .demo-note {
          font-size: var(--text-xs);
          color: var(--text-muted);
          padding: var(--space-3);
          background: var(--bg-glass);
          border-radius: var(--radius-md);
        }
        
        @media (max-width: 1024px) {
          .studio-layout {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
        </div>
    )
}
